import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios';
import { startAiLicenseWatch } from './services/licenseClient';
import logo from '../images/Flexpos.jpg'
import backImage from '../images/back.png'
import Stack from '@mui/material/Stack';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import {
    // `changetaffpassword` was imported here (note the missing 's') but BaseURL
    // has never exported that name, and nothing in this file used it — a dead
    // import producing a webpack warning on every build. Removed. The real
    // export is `changestaffpassword`; re-add that if this screen ever needs it.
    companyInfo, URL,
    loadAllSOPDocumentsdb, loadSOPDocumentByIddb
} from './BaseURL';
import FirstLoginDialog from './FirstLoginDialog ';
import LoginSwirlBackground from './Login/LoginSwirlBackground';

import { socket } from './services/socket';

import {
    TextField, Button, Alert, Fade, CircularProgress,
    InputAdornment, Slide, Zoom, Grow,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Chip, Typography, Box, Divider, IconButton,
    Paper, Stepper, Step, StepLabel, StepContent,
} from '@mui/material';

import {
    Visibility, VisibilityOff, CheckCircleOutline,
    Close as CloseIcon,
    MenuBook as MenuBookIcon,
    ArrowForwardIos as ArrowIcon,
    GridView as GridViewIcon,
    NavigateNext, NavigateBefore,
} from '@mui/icons-material';

import {
    LocalHospital as Hospital,
    Person as User,
    Lock,
    Security,
    VerifiedUser,
} from '@mui/icons-material';

// ─── colours (same as your existing Login) ───────────────────────────────────
const PRIMARY_COLOR = '#1A9B7F';
const PRIMARY_DARK  = '#157763';
const PRIMARY_LIGHT = '#20B894';
const ACCENT_COLOR  = '#0E6B58';

const TYPE_COLORS  = { SOP: '#1A9B7F', Policy: '#2563eb' };
const STATUS_COLOR = { Active: '#16a34a', Draft: '#d97706', 'Under Review': '#ea580c', Archived: '#6b7280' };

// ─── normalise DB row ─────────────────────────────────────────────────────────
function normaliseDoc(row) {
    return {
        ...row,
        id:            Number(row.id),
        tags:          row.tags ? row.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        steps:         row.steps || [],
        review_date:   row.review_date ? row.review_date.split('T')[0] : '',
        categoryLabel: row.category_label || row.category,
    };
}

// ═════════════════════════════════════════════════════════════════════════════
//  SOP STEP DIALOG  — shows full steps for one document (read-only)
// ═════════════════════════════════════════════════════════════════════════════
function SOPStepDialog({ open, doc, onClose }) {
    if (!doc) return null;
    const color = TYPE_COLORS[doc.type] || PRIMARY_COLOR;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

            {/* header */}
            <Box sx={{ bgcolor: color, px: 3, py: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box flex={1} pr={2}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Chip label={doc.type} size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, fontSize: 11 }} />
                            <Chip label={doc.status} size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 10 }} />
                            <Chip label={doc.version} size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 10 }} />
                        </Box>
                        <Typography fontWeight={700} fontSize={16} color="#fff" lineHeight={1.3}>
                            {doc.title}
                        </Typography>
                        <Typography fontSize={12} color="rgba(255,255,255,0.8)" mt={0.5}>
                            {doc.categoryLabel} &bull; {doc.department}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}
                        sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: '#F7F9FB' }}>
                {/* description */}
                {doc.description && (
                    <Box sx={{ px: 3, py: 2, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
                        <Typography fontSize={13} color="text.secondary" lineHeight={1.7}>
                            {doc.description}
                        </Typography>
                    </Box>
                )}

                {/* steps */}
                <Box sx={{ px: 3, py: 2.5 }}>
                    <Typography fontSize={12} fontWeight={700} color={color}
                        textTransform="uppercase" letterSpacing={0.8} mb={2}>
                        {doc.type === 'SOP' ? `Procedure Steps (${doc.steps.length})` : `Policy Sections (${doc.steps.length})`}
                    </Typography>

                    {doc.steps.length === 0 ? (
                        <Typography fontSize={13} color="text.disabled" fontStyle="italic">
                            No steps available for this document.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {doc.steps.map((step, i) => (
                                <Paper key={i} elevation={0} sx={{
                                    borderRadius: 2.5, overflow: 'hidden',
                                    border: '1px solid', borderColor: `${color}30`,
                                }}>
                                    <Box sx={{ px: 2, py: 1, bgcolor: `${color}0D`, borderBottom: `1px solid ${color}20`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{
                                            width: 24, height: 24, borderRadius: '50%',
                                            bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}>
                                            <Typography fontSize={11} fontWeight={700} color="#fff">{i + 1}</Typography>
                                        </Box>
                                        <Typography fontSize={13} fontWeight={700} color={color}>
                                            {step.label}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fff' }}>
                                        <Typography fontSize={13} color="text.secondary" lineHeight={1.75}>
                                            {step.content}
                                        </Typography>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* meta footer */}
                {(doc.author || doc.approved_by || doc.review_date) && (
                    <Box sx={{ px: 3, pb: 2.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {doc.author && (
                            <Box>
                                <Typography fontSize={10} color="text.disabled" textTransform="uppercase" letterSpacing={0.5}>Author</Typography>
                                <Typography fontSize={12} fontWeight={600}>{doc.author}</Typography>
                            </Box>
                        )}
                        {doc.approved_by && (
                            <Box>
                                <Typography fontSize={10} color="text.disabled" textTransform="uppercase" letterSpacing={0.5}>Approved by</Typography>
                                <Typography fontSize={12} fontWeight={600}>{doc.approved_by}</Typography>
                            </Box>
                        )}
                        {doc.review_date && (
                            <Box>
                                <Typography fontSize={10} color="text.disabled" textTransform="uppercase" letterSpacing={0.5}>Review Date</Typography>
                                <Typography fontSize={12} fontWeight={600}>{doc.review_date}</Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#fff', borderTop: '1px solid #e2e8f0' }}>
                <Typography fontSize={11} color="text.disabled" flex={1}>
                    Read-only &bull; TotalCare HIS
                </Typography>
                <Button onClick={onClose} variant="contained"
                    sx={{ bgcolor: color, '&:hover': { filter: 'brightness(0.88)' }, borderRadius: 2, px: 3 }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ═════════════════════════════════════════════════════════════════════════════
//  VIEW-ALL DIALOG  — grid of all SOPs, click one to open step dialog
// ═════════════════════════════════════════════════════════════════════════════
function SOPListDialog({ open, docs, onClose, onSelect, loadingId }) {
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('All');
    const filtered = docs.filter(d => {
        const s = search.toLowerCase();
        return (!s || d.title.toLowerCase().includes(s) || (d.categoryLabel || '').toLowerCase().includes(s)) &&
            (filterType === 'All' || d.type === filterType);
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', height: '85vh' } }}>

            <Box sx={{ bgcolor: PRIMARY_COLOR, px: 3, py: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography fontWeight={700} fontSize={17} color="#fff">Policy &amp; SOP Library</Typography>
                        <Typography fontSize={12} color="rgba(255,255,255,0.75)">{docs.length} documents available</Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}
                        sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* search + filter bar */}
            <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <TextField size="small" placeholder="Search documents…" value={search}
                    onChange={e => setSearch(e.target.value)} sx={{ flex: 1 }} />
                {['All', 'SOP', 'Policy'].map(t => (
                    <Chip key={t} label={t} size="small" clickable
                        onClick={() => setFilterType(t)}
                        sx={{
                            fontWeight: 600, fontSize: 11,
                            bgcolor: filterType === t ? TYPE_COLORS[t] || PRIMARY_COLOR : 'transparent',
                            color: filterType === t ? '#fff' : 'text.secondary',
                            border: '1px solid',
                            borderColor: filterType === t ? 'transparent' : '#e2e8f0',
                        }} />
                ))}
            </Box>

            <DialogContent sx={{ p: 2.5, bgcolor: '#F7F9FB', overflowY: 'auto' }}>
                {filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <MenuBookIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                        <Typography color="text.disabled">No documents match your search</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
                        {filtered.map(doc => (
                            <Paper key={doc.id} elevation={0} onClick={() => onSelect(doc.id)}
                                sx={{
                                    borderRadius: 3, border: '1.5px solid #e2e8f0', overflow: 'hidden', cursor: 'pointer',
                                    transition: 'all 0.18s ease',
                                    '&:hover': { borderColor: TYPE_COLORS[doc.type], boxShadow: '0 4px 16px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' },
                                }}>
                                <Box sx={{ height: 4, bgcolor: TYPE_COLORS[doc.type] }} />
                                <Box sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Chip label={doc.type} size="small"
                                            sx={{ bgcolor: TYPE_COLORS[doc.type], color: '#fff', fontWeight: 700, fontSize: 10, height: 20 }} />
                                        <Typography fontSize={10} color="text.disabled">{doc.version}</Typography>
                                    </Box>
                                    <Typography fontSize={13.5} fontWeight={700} lineHeight={1.35} mb={0.5}
                                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {doc.title}
                                    </Typography>
                                    <Typography fontSize={11} color="text.disabled" mb={1.5}>{doc.categoryLabel}</Typography>
                                    <Typography fontSize={12} color="text.secondary" lineHeight={1.6}
                                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {doc.description}
                                    </Typography>
                                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button size="small" variant="contained" endIcon={loadingId === doc.id ? <CircularProgress size={12} color="inherit" /> : <ArrowIcon sx={{ fontSize: 12 }} />}
                                            sx={{ bgcolor: TYPE_COLORS[doc.type], '&:hover': { filter: 'brightness(0.88)' }, fontSize: 11, borderRadius: 1.5, px: 1.5, height: 28 }}>
                                            {loadingId === doc.id ? 'Loading…' : 'View'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ═════════════════════════════════════════════════════════════════════════════
//  ROTATING SOP CARD  — premium glassmorphism version
// ═════════════════════════════════════════════════════════════════════════════
function RotatingSOPCard({ docs, onView, onViewAll }) {
    const [idx,     setIdx]     = useState(0);
    const [visible, setVisible] = useState(true);
    const [hovered, setHovered] = useState(false);
    const timerRef = useRef(null);

    const resetTimer = () => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(advance, 4500);
    };

    const advance = () => {
        setVisible(false);
        setTimeout(() => { setIdx(i => (i + 1) % docs.length); setVisible(true); }, 380);
    };

    useEffect(() => {
        if (docs.length < 2) return;
        timerRef.current = setInterval(advance, 4500);
        return () => clearInterval(timerRef.current);
    }, [docs.length]);

    const go = (dir) => {
        setVisible(false);
        setTimeout(() => { setIdx(i => (i + dir + docs.length) % docs.length); setVisible(true); }, 300);
        resetTimer();
    };

    const jumpTo = (i) => {
        setVisible(false);
        setTimeout(() => { setIdx(i); setVisible(true); }, 300);
        resetTimer();
    };

    if (docs.length === 0) return null;
    const doc      = docs[idx];
    const color    = TYPE_COLORS[doc.type] || PRIMARY_COLOR;
    const stepCount = Number(doc.step_count) || doc.steps?.length || 0;

    // stats strip from docs
    const totalSOP    = docs.filter(d => d.type === 'SOP').length;
    const totalPolicy = docs.filter(d => d.type === 'Policy').length;
    const totalActive = docs.filter(d => d.status === 'Active').length;

    return (
        <div style={{ width: '100%', maxWidth: 420 }}>

            {/* ── header row ── */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:'rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <MenuBookIcon style={{ fontSize:18, color:'#fff' }} />
                    </div>
                    <div>
                        <div style={{ fontSize:13, fontWeight:800, color:'#fff', letterSpacing:1.2, textTransform:'uppercase', lineHeight:1 }}>Policy &amp; SOP Library</div>
                        <div style={{ fontSize:11.5, color:'rgba(255,255,255,0.55)', marginTop:2 }}>{docs.length} documents</div>
                    </div>
                </div>
                <button
                    onClick={onViewAll}
                    onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.28)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; e.currentTarget.style.transform='translateY(0)'; }}
                    style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:20, color:'#fff', fontSize:12.5, fontWeight:700, padding:'6px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:5, backdropFilter:'blur(8px)', transition:'all 0.2s' }}>
                    <GridViewIcon style={{ fontSize:14 }} /> Browse All
                </button>
            </div>

            {/* ── stats strip ── */}
            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
                {[
                    { label:'SOPs', val: totalSOP,    icon:'📋' },
                    { label:'Policies', val: totalPolicy, icon:'📜' },
                    { label:'Active', val: totalActive,  icon:'✅' },
                ].map(s => (
                    <div key={s.label} style={{ flex:1, background:'rgba(255,255,255,0.1)', backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,0.18)', borderRadius:10, padding:'8px 10px', textAlign:'center' }}>
                        <div style={{ fontSize:16 }}>{s.icon}</div>
                        <div style={{ fontSize:18, fontWeight:800, color:'#fff', lineHeight:1.1 }}>{s.val}</div>
                        <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', fontWeight:600, letterSpacing:0.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* ── main card ── */}
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    borderRadius: 18,
                    overflow: 'hidden',
                    border: `1px solid ${hovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)'}`,
                    boxShadow: hovered
                        ? '0 20px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1)'
                        : '0 10px 30px rgba(0,0,0,0.18)',
                    opacity:   visible ? 1 : 0,
                    transform: visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.98)',
                    transition: 'opacity 0.38s ease, transform 0.38s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    background: 'rgba(0,0,0,0.18)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                }}>

                {/* gradient top bar */}
                <div style={{ height:5, background: doc.type === 'Policy'
                    ? 'linear-gradient(90deg, #2563eb, #7c3aed)'
                    : 'linear-gradient(90deg, #1A9B7F, #20B894, #059669)',
                }} />

                {/* card body */}
                <div style={{ padding:'18px 20px 14px' }}>

                    {/* row 1: badges + step pill */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                        <div style={{ display:'flex', gap:5 }}>
                            <span style={{ background: color, color:'#fff', fontSize:11.5, fontWeight:800, padding:'3px 10px', borderRadius:12, letterSpacing:0.3 }}>
                                {doc.type}
                            </span>
                            <span style={{ background:'rgba(255,255,255,0.2)', color:'#fff', fontSize:11.5, fontWeight:600, padding:'3px 10px', borderRadius:12 }}>
                                {doc.status}
                            </span>
                            <span style={{ background:'rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.75)', fontSize:11.5, padding:'3px 10px', borderRadius:12 }}>
                                {doc.version}
                            </span>
                        </div>
                        {stepCount > 0 && (
                            <span style={{ background:'rgba(255,255,255,0.14)', color:'rgba(255,255,255,0.85)', fontSize:11.5, fontWeight:600, padding:'3px 10px', borderRadius:12, border:'1px solid rgba(255,255,255,0.2)' }}>
                                {stepCount} step{stepCount !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>

                    {/* title */}
                    <div style={{ fontSize:17, fontWeight:800, color:'#fff', lineHeight:1.3, marginBottom:6, textShadow:'0 1px 3px rgba(0,0,0,0.2)' }}>
                        {doc.title}
                    </div>

                    {/* category */}
                    <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.65)', marginBottom:10, display:'flex', alignItems:'center', gap:5 }}>
                        <span style={{ width:6, height:6, borderRadius:'50%', background:'rgba(255,255,255,0.4)', display:'inline-block', flexShrink:0 }} />
                        {doc.categoryLabel} &nbsp;·&nbsp; {doc.department || 'All Departments'}
                    </div>

                    {/* description */}
                    <div style={{ fontSize:13.5, color:'rgba(255,255,255,0.87)', lineHeight:1.7, marginBottom:12, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                        {doc.description || 'No description available.'}
                    </div>

                    {/* tags */}
                    {doc.tags?.length > 0 && (
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:14 }}>
                            {doc.tags.slice(0, 4).map(t => (
                                <span key={t} style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', color:'rgba(255,255,255,0.88)', fontSize:11.5, padding:'3px 10px', borderRadius:10, fontWeight:500 }}>
                                    #{t}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* divider */}
                    <div style={{ height:1, background:'rgba(255,255,255,0.12)', marginBottom:12 }} />

                    {/* action row */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        {/* prev/next */}
                        <div style={{ display:'flex', gap:6 }}>
                            {[[-1, <NavigateBefore style={{fontSize:16}} />], [1, <NavigateNext style={{fontSize:16}} />]].map(([dir, icon]) => (
                                <button key={dir} onClick={() => go(dir)}
                                    onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
                                    onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.16)'}
                                    style={{ background:'rgba(255,255,255,0.16)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:9, color:'#fff', width:30, height:30, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'background 0.2s' }}>
                                    {icon}
                                </button>
                            ))}
                        </div>

                        {/* position counter */}
                        <span style={{ fontSize:13, color:'rgba(255,255,255,0.45)', fontWeight:600 }}>
                            {idx + 1} / {docs.length}
                        </span>

                        {/* read button */}
                        <button onClick={() => onView(doc.id)}
                            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 18px rgba(0,0,0,0.25)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 3px 10px rgba(0,0,0,0.15)'; }}
                            style={{ background:'#fff', border:'none', borderRadius:10, color: color, fontSize:14, fontWeight:800, padding:'9px 22px', cursor:'pointer', display:'flex', alignItems:'center', gap:5, boxShadow:'0 3px 10px rgba(0,0,0,0.15)', transition:'all 0.22s ease' }}>
                            Read <ArrowIcon style={{ fontSize:12 }} />
                        </button>
                    </div>
                </div>

                {/* progress bar at bottom */}
                <div style={{ height:3, background:'rgba(255,255,255,0.1)' }}>
                    <div style={{ height:'100%', width:`${((idx + 1) / docs.length) * 100}%`, background:`linear-gradient(90deg, ${color}, rgba(255,255,255,0.7))`, transition:'width 0.4s ease', borderRadius:'0 2px 2px 0' }} />
                </div>
            </div>

            {/* dots */}
            <div style={{ display:'flex', justifyContent:'center', gap:5, marginTop:10 }}>
                {docs.slice(0, Math.min(docs.length, 10)).map((_, i) => (
                    <div key={i} onClick={() => jumpTo(i)}
                        style={{ width: i === idx % Math.min(docs.length, 10) ? 22 : 7, height:7, borderRadius:4, cursor:'pointer', background: i === idx % Math.min(docs.length, 10) ? '#fff' : 'rgba(255,255,255,0.3)', transition:'all 0.3s ease', boxShadow: i === idx % Math.min(docs.length, 10) ? '0 0 8px rgba(255,255,255,0.5)' : 'none' }} />
                ))}
            </div>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════════════════
//  LOGIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function Login() {
    const navigate = useNavigate();
    const [showPassword, setShowPassword]   = useState(false);
    const [companylogo,  setcompanylogo]    = useState('');
    const [companyname,  setcompanyName]    = useState('');
    const [company,      setcompany]        = useState([]);
    const [loading,      setLoading]        = useState(false);
    const [mounted,      setMounted]        = useState(false);
    const [formData,     setFormData]       = useState({ username: '', password: '' });
    const [usernameError, setusernameError] = useState(false);
    const [passwordError, setpasswordError] = useState(false);
    const [error,         setError]         = useState('');
    const [success,       setSuccess]       = useState(false);
    const [showFirstLogin, setShowFirstLogin]   = useState(false);
    const [firstLoginEmail,setFirstLoginEmail]  = useState('');
    const [pendingUserData,setPendingUserData]  = useState(null);

    // ── SOP state ──────────────────────────────────────────────────────────
    const [sopDocs,        setSOPDocs]        = useState([]);
    const [sopViewDoc,     setSOPViewDoc]     = useState(null);   // full doc for step dialog
    const [sopViewOpen,    setSOPViewOpen]    = useState(false);
    const [sopListOpen,    setSOPListOpen]    = useState(false);
    const [sopLoadingId,   setSOPLoadingId]   = useState(null);
    const [sopLoadingList, setSOPLoadingList] = useState(false);

    useEffect(() => { setMounted(true); }, []);

    // company info
    const loadCompany = async () => {
        try {
            const { data } = await axios.get(companyInfo);
            if (data.success) setcompany(data.results || []);
        } catch (err) {
            console.error('loadCompany:', err);
        }
    };
    useEffect(() => { loadCompany(); }, []);
    useEffect(() => {
        if (Array.isArray(company) && company.length > 0) {
            setcompanyName(company[0].name);
            setcompanylogo(company[0].logo);
            localStorage.setItem('companyInfo', JSON.stringify(company[0]));
        }
    }, [company]);

    // ── load SOP list ──────────────────────────────────────────────────────
    useEffect(() => {
        const fetchSOPs = async () => {
            setSOPLoadingList(true);
            try {
                const { data } = await axios.get(loadAllSOPDocumentsdb);
                if (data.success) setSOPDocs(data.data.map(normaliseDoc));
            } catch (err) {
                console.error('SOP load error:', err);
            } finally {
                setSOPLoadingList(false);
            }
        };
        fetchSOPs();
    }, []);

    // ── open step dialog for one doc ──────────────────────────────────────
    async function handleViewSOP(id) {
        setSOPLoadingId(id);
        try {
            const { data } = await axios.get(loadSOPDocumentByIddb, { params: { id } });
            if (data.success) {
                setSOPViewDoc(normaliseDoc(data.data));
                setSOPViewOpen(true);
                setSOPListOpen(false);
            }
        } catch (err) {
            console.error('SOPViewDoc error:', err);
        } finally {
            setSOPLoadingId(null);
        }
    }

    // ── login submit ──────────────────────────────────────────────────────
const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
        const { data } = await axios.post(URL + 'login', {
            username: formData.username,
            password: formData.password,
        });

        if (data.success) {
            const user = data.user;

            if (user.first_login) {
                setFirstLoginEmail(user.email);
                setPendingUserData(user);
                setShowFirstLogin(true);
                setLoading(false);
                return;
            }

            // ── LICENSE EXPIRY WARNING (≤ 90 days) — login still proceeds ──
            if (data.licenseWarning && data.daysLeft) {
                const d = data.daysLeft;
                const urgencyColor = d <= 7  ? '#dc2626'
                                   : d <= 30 ? '#ea580c'
                                   : '#d97706';

                await Swal.fire({
                    icon:  d <= 30 ? 'error' : 'warning',
                    title: d <= 7  ? '⚠️ License Expiring Very Soon!'
                                   : '📅 License Expiry Notice',
                    html: `
                        <div style="text-align:center; padding:8px 0">
                            <div style="
                                display:inline-block;
                                background:${urgencyColor};
                                color:#fff;
                                font-size:40px;
                                font-weight:900;
                                padding:12px 28px;
                                border-radius:12px;
                                margin-bottom:12px;
                            ">${d}</div>
                            <p style="color:#374151; font-size:15px; font-weight:600; margin:0 0 6px">
                                day${d === 1 ? '' : 's'} remaining
                            </p>
                            <p style="color:#6b7280; font-size:13px; margin:0 0 14px">
                                Your TotalCare system license will expire soon.
                            </p>
                            <div style="
                                background:#fef3c7;
                                border:1px solid #fbbf24;
                                border-radius:10px;
                                padding:10px 14px;
                                text-align:left;
                                font-size:13px;
                                color:#92400e;
                            ">
                                <strong>Action required:</strong> Contact TotalCare support 
                                to renew before expiry to avoid service interruption.
                            </div>
                        </div>
                    `,
                    confirmButtonText:  'Continue to Dashboard',
                    confirmButtonColor: urgencyColor,
                    allowOutsideClick:  false,
                });
            }
            // ──────────────────────────────────────────────────────────────

            completeLogin(user);
        }

    } catch (err) {
        setLoading(false);

        const data   = err.response?.data;
        const status = err.response?.status;

        if (data?.branchBlocked) {
            await Swal.fire({
                icon:               'warning',
                title:              'Branch not active',
                html: `
                    <div style="text-align:center; padding:10px 0">
                        <p style="color:#64748b; font-size:14px; margin-bottom:8px">
                            ${data?.message || 'Your branch is not currently active.'}
                        </p>
                        <p style="color:#94a3b8; font-size:12px">
                            A System Administrator must approve / reactivate the branch before you can log in.
                        </p>
                    </div>
                `,
                confirmButtonText:  'OK',
                confirmButtonColor: '#b45309',
            });
            return;
        }

        if (status === 403 || data?.licenseBlocked) {
            await Swal.fire({
                icon:             'error',
                title:            '🔒 System License Expired',
                html: `
                    <div style="text-align:center; padding:10px 0">
                        <p style="color:#64748b; font-size:14px; margin-bottom:8px">
                            ${data?.message || 'Your license has expired or been suspended.'}
                        </p>
                        <p style="color:#94a3b8; font-size:12px">
                            Please contact TotalCare support to renew your license.
                        </p>
                    </div>
                `,
                confirmButtonText:  'OK',
                confirmButtonColor: '#d33',
                allowOutsideClick:  false,
                allowEscapeKey:     false,
            });
            return;
        }

        setError(data?.message || 'Invalid username or password');
    }
};

const completeLogin = (user) => {
    setSuccess(true);

    const userInfo = {
        name:       user.name,
        email:      user.email,
        department: user.department,
        role:       user.role,
        staff_id:   user.staff_id,
        is_staff:   user.is_staff,
        trans_code: user.id,
        username:   user.username,
        created_at: user.created_at,
        branch:     user.branch || null,   // { id, code, name, location, isMain }
    };

    localStorage.setItem('userInfo', JSON.stringify(userInfo));

    // ── Default license states until server responds via socket ──────────
    localStorage.setItem('license_ai',        JSON.stringify({ allowed: true }));
    localStorage.setItem('license_call_chat', JSON.stringify({ allowed: true }));

    try {
        if (!socket.connected) {
            socket.connect();
        }

        const registerUser = () => {
            socket.emit('register-user', {
                userId: String(user.id),
            });
        };

        if (socket.connected) {
            registerUser();
        } else {
            socket.once('connect', registerUser);
        }

        // ── Listen to license events sent back by the server ─────────────
        socket.off('ai-license');
        socket.off('call-chat-license');

        socket.on('ai-license', (data) => {
            console.log('[LICENSE] ai-license received:', data);
            localStorage.setItem('license_ai', JSON.stringify(data));
        });

        socket.on('call-chat-license', (data) => {
            console.log('[LICENSE] call-chat-license received:', data);
            localStorage.setItem('license_call_chat', JSON.stringify(data));
        });
        // ─────────────────────────────────────────────────────────────────

        //  And straight from /ai/status, refreshed every five minutes, so the AI
        //  buttons follow the facility's licence even if the socket is slow.
        startAiLicenseWatch();

    } catch (err) {
        console.error('Socket registration error:', err);
    }

    // ── Resume previous panel if auto-logged-out recently (same user, same day, < 1h) ──
    const RESUME_WINDOW_MS = 60 * 60 * 1000;
    let resumeRoute = '/dashboard';
    try {
        const raw = localStorage.getItem('lastSession');
        if (raw) {
            const last = JSON.parse(raw);
            const sameUser    = last?.username === user.username;
            const sameDay     = last?.logoutDate === new Date().toDateString();
            const withinWindow = Date.now() - (last?.logoutTime || 0) < RESUME_WINDOW_MS;
            const validRoute  = typeof last?.route === 'string'
                && last.route.startsWith('/dashboard/')
                && last.route !== '/dashboard/';
            if (sameUser && sameDay && withinWindow && validRoute) {
                resumeRoute = last.route;
            }
        }
    } catch (e) {
        // ignore — fall back to default
    }
    localStorage.removeItem('lastSession');

    setTimeout(() => navigate(resumeRoute), 1000);
};

useEffect(() => {
    return () => {
        socket.off('connect');
    };
}, []);

    const usernameErrorhandel = () => setusernameError(!formData.username);
    const passwordErrorhandel = () => setpasswordError(!formData.password);

    // ══════════════════════════════════════════════════════════════════════
    return (
        <div className="container-fluid main_pane p-0"
            style={{ minHeight: '100vh', background: '#ffffff', overflow: 'hidden', fontFamily: '"Inter","Segoe UI","Helvetica Neue",Arial,sans-serif' }}>

            <style>{`
                @keyframes fadeInUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                @keyframes softPulse { 0%,100%{box-shadow:0 0 0 0 rgba(74,222,128,.55)} 50%{box-shadow:0 0 0 8px rgba(74,222,128,0)} }
                .animate-fade-in-up { animation:fadeInUp .6s ease-out forwards; }
                .live-dot { animation: softPulse 2.4s ease-in-out infinite; }
                .login-input .MuiOutlinedInput-root { border-radius:10px; background:#fff; transition:border-color .15s ease, box-shadow .15s ease; }
                .login-input .MuiOutlinedInput-notchedOutline { border-color:#dde3ea; }
                .login-input .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color:${PRIMARY_COLOR}; }
                .login-input .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color:${PRIMARY_COLOR}; border-width:1.5px; }
                .login-input .MuiOutlinedInput-root.Mui-focused { box-shadow: 0 0 0 4px rgba(26,155,127,.10); }
                .login-input .MuiInputLabel-root.Mui-focused { color:${PRIMARY_COLOR}; font-weight:600; }
                .login_bu { position:relative; overflow:hidden; }
                .login_bu::before { content:''; position:absolute; top:0; left:-100%; width:100%; height:100%; background:linear-gradient(90deg,transparent,rgba(255,255,255,.3),transparent); transition:left .6s; }
                .login_bu:hover::before { left:100%; }
                .trust-chip { display:flex; align-items:center; justify-content:center; gap:6px; flex:1; padding:8px 10px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.14); border-radius:8px; font-size:11.5px; font-weight:600; color:rgba(255,255,255,0.85); letter-spacing:.2px; }
                .footer-link { font-size:12px; color:rgba(255,255,255,0.45); text-decoration:none; font-weight:500; transition:color .2s ease; }
                .footer-link:hover { color:rgba(255,255,255,0.9); }
            `}</style>

            <div className="container-fluid h-100 p-0">
                <div className="row g-0 h-100">

                    {/* ══════════════════════════════════════════════════════
                        LEFT SIDE — clean clinical brand panel
                    ══════════════════════════════════════════════════════ */}
                    <div className="col-lg-6 d-none d-lg-flex flex-column justify-content-start align-items-center p-0"
                        style={{
                            background: 'linear-gradient(180deg, #0E6B58 0%, #0A5244 100%)',
                            color: 'white', overflowY: 'auto', position: 'relative', minHeight: '100vh',
                        }}>

                        {/* subtle medical-cross watermark + soft top glow */}
                        <div aria-hidden style={{ position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none', zIndex:0 }}>
                            <div style={{
                                position:'absolute', top:'-10%', right:'-12%', width:520, height:520, borderRadius:'50%',
                                background:'radial-gradient(circle, rgba(32,184,148,0.22) 0%, transparent 65%)',
                            }} />
                            <svg width="280" height="280" viewBox="0 0 100 100"
                                style={{ position:'absolute', right:-30, bottom:-40, opacity:0.05 }}>
                                <path fill="#fff" d="M40 0h20v40h40v20H60v40H40V60H0V40h40z" />
                            </svg>
                            <div style={{
                                position:'absolute', inset:0,
                                backgroundImage:'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)',
                                backgroundSize:'48px 48px',
                            }} />
                        </div>

                        <div style={{ width:'100%', maxWidth:480, padding:'48px 40px 36px', position:'relative', zIndex:1 }}>

                            {/* ── BRAND HEADER ── */}
                            <div className="animate-fade-in-up" style={{ animationDelay:'.05s', opacity:0, marginBottom:32 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:18 }}>
                                    <div style={{ position:'relative', flexShrink:0 }}>
                                        <div style={{
                                            width:52, height:52, borderRadius:12,
                                            background:'rgba(255,255,255,0.10)',
                                            border:'1px solid rgba(255,255,255,0.22)',
                                            display:'flex', alignItems:'center', justifyContent:'center',
                                        }}>
                                            <Hospital sx={{ fontSize:28, color:'#fff' }} />
                                        </div>
                                        <span className="live-dot" style={{
                                            position:'absolute', top:-2, right:-2, width:10, height:10, borderRadius:'50%',
                                            background:'#4ade80', border:'2px solid #0A5244',
                                        }} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize:11, color:'rgba(255,255,255,0.55)', fontWeight:600, letterSpacing:1.6, textTransform:'uppercase', marginBottom:4 }}>
                                            TotalCare Hospital Information System
                                        </div>
                                        <div style={{ fontSize:'clamp(1.6rem,2.8vw,2.1rem)', fontWeight:800, color:'#fff', lineHeight:1.15, letterSpacing:-0.3 }}>
                                            {companyname || 'Smart Hospital'}
                                        </div>
                                    </div>
                                </div>

                                <p style={{ margin:0, fontSize:14.5, color:'rgba(255,255,255,0.78)', lineHeight:1.7, maxWidth:440 }}>
                                    A unified clinical and administrative platform — admissions, claims, pharmacy, lab, and reporting in one secure environment.
                                </p>
                            </div>

                            {/* ── TRUST CHIPS ── */}
                            <div className="animate-fade-in-up" style={{ animationDelay:'.15s', opacity:0, display:'flex', gap:8, marginBottom:28 }}>
                                <div className="trust-chip"><Security fontSize="small" /><span>HIPAA Compliant</span></div>
                                <div className="trust-chip"><VerifiedUser fontSize="small" /><span>ISO Certified</span></div>
                                <div className="trust-chip"><Lock fontSize="small" /><span>256-bit SSL</span></div>
                            </div>

                            {/* ── ROTATING SOP CARD ── */}
                            <div className="animate-fade-in-up" style={{ animationDelay:'.25s', opacity:0, marginBottom:28 }}>
                                {sopLoadingList ? (
                                    <div style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:14, padding:'24px 20px', display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
                                        <CircularProgress size={22} sx={{ color:'rgba(255,255,255,0.7)' }} />
                                        <span style={{ fontSize:13, color:'rgba(255,255,255,0.55)' }}>Loading Policy &amp; SOP Library…</span>
                                    </div>
                                ) : sopDocs.length > 0 ? (
                                    <RotatingSOPCard
                                        docs={sopDocs}
                                        onView={handleViewSOP}
                                        onViewAll={() => setSOPListOpen(true)}
                                    />
                                ) : null}
                            </div>

                            {/* ── FOOTER ── */}
                            <div className="animate-fade-in-up" style={{ animationDelay:'.4s', opacity:0, marginTop:24, paddingTop:18, borderTop:'1px solid rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                <span style={{ fontSize:12, color:'rgba(255,255,255,0.4)' }}>© {new Date().getFullYear()} TotalCare HIS</span>
                                <div style={{ display:'flex', gap:18 }}>
                                    {['Privacy', 'Terms', 'Support'].map(l => (
                                        <a key={l} href="#" className="footer-link">{l}</a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ══════════════════════════════════════════════════════
                        RIGHT SIDE — refined login form
                    ══════════════════════════════════════════════════════ */}
                    <div className="col-12 col-lg-6 d-flex flex-column justify-content-center p-3 p-lg-4"
                        style={{ background:'#ffffff', minHeight:'100vh', position:'relative', overflow:'hidden' }}>

                        {/* Mouse-reactive ribbon background.
                            Tuned to match the Webuzo aesthetic the user
                            referenced: muted earth-tone palette, long
                            flowing curves that swirl around the cursor.
                            Background stays white per the prior request;
                            the longer particle life and lower trail wipe
                            give each ribbon a long persistent path so the
                            mouse-follow looks like a pen tracing curls. */}
                        <LoginSwirlBackground
                            background="#ffffff"
                            trailColor="255, 255, 255"
                            trail={0.022}
                            segmentAlpha={0.55}
                            lineWidth={1.3}
                            count={90}
                            swirlRadius={440}
                            swirlStrength={0.07}
                            damping={0.992}
                            palette={['#0E6B58', '#3a6b8a', '#9c4a3a', '#7a8c4a', '#b87333', '#6a5a8a']}
                        />

                        {/* very subtle top accent line */}
                        <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${PRIMARY_COLOR},${PRIMARY_LIGHT},${PRIMARY_COLOR})`, zIndex:1 }} />

                        <div style={{ maxWidth:400, margin:'0 auto', width:'100%', position:'relative', zIndex:1 }}>

                            {/* mobile brand */}
                            <div className="d-block d-lg-none text-center mb-4 animate-fade-in-up" style={{ opacity: mounted ? 1 : 0 }}>
                                <h2 style={{ color:PRIMARY_COLOR, fontWeight:800, fontSize:'1.85rem', marginBottom:'0.25rem' }}>TotalCare</h2>
                                <p style={{ color:'#64748b', fontSize:'0.85rem', margin:0 }}>{companyname || 'Hospital'} — Hospital Information System</p>
                            </div>

                            {/* logo + heading */}
                            <div className="text-center mb-4 animate-fade-in-up" style={{ display:'flex', flexDirection:'column', alignItems:'center', opacity: mounted ? 1 : 0, animationDelay:'.1s' }}>
                                <div style={{
                                    width:64, height:64, borderRadius:16,
                                    background:`linear-gradient(135deg,${PRIMARY_COLOR} 0%,${PRIMARY_DARK} 100%)`,
                                    display:'flex', alignItems:'center', justifyContent:'center',
                                    boxShadow:'0 6px 18px rgba(26,155,127,.25)',
                                    marginBottom:18,
                                }}>
                                    <Hospital sx={{ fontSize:32, color:'#fff' }} />
                                </div>
                                <h2 style={{ color:'#0f172a', fontWeight:700, marginBottom:'.4rem', fontSize:'1.6rem', letterSpacing:'-0.3px' }}>
                                    Welcome back
                                </h2>
                                <p style={{ color:'#64748b', fontSize:'.93rem', fontWeight:500, margin:0 }}>
                                    Sign in to access your clinical workspace
                                </p>
                            </div>

                            {/* alerts */}
                            {success ? (
                                <Slide direction="down" in={success} mountOnEnter unmountOnExit>
                                    <Alert icon={<CheckCircleOutline fontSize="inherit" />} severity="success" sx={{ mb:3, borderRadius:2 }}>
                                        <strong>Success.</strong> Redirecting to your dashboard…
                                    </Alert>
                                </Slide>
                            ) : error && (
                                <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                                    <Alert severity="error" sx={{ mb:3, borderRadius:2 }}>
                                        <strong>Error.</strong> {error}
                                    </Alert>
                                </Slide>
                            )}

                            {/* form */}
                            <form className="animate-fade-in-up" onSubmit={handleSubmit} style={{ opacity: mounted ? 1 : 0, animationDelay:'.2s' }}>
                                <TextField
                                    label="Username" variant="outlined" fullWidth margin="normal"
                                    className="login-input"
                                    value={formData.username}
                                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    required error={usernameError} onBlur={usernameErrorhandel}
                                    InputProps={{ startAdornment:<InputAdornment position="start"><User style={{ color:PRIMARY_COLOR, fontSize:20 }} /></InputAdornment> }}
                                />

                                <TextField
                                    label="Password" variant="outlined" type={showPassword ? 'text' : 'password'} fullWidth margin="normal"
                                    className="login-input"
                                    error={passwordError} onBlur={passwordErrorhandel}
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    InputProps={{
                                        startAdornment:<InputAdornment position="start"><Lock style={{ color:PRIMARY_COLOR, fontSize:20 }} /></InputAdornment>,
                                        endAdornment:(
                                            <IconButton size="small" onClick={() => setShowPassword(p => !p)} sx={{ color:'#94a3b8', '&:hover':{ color:PRIMARY_COLOR, backgroundColor:'rgba(26,155,127,.06)' } }}>
                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        ),
                                    }}
                                />

                                <Button
                                    variant="contained" fullWidth className="mt-4 login_bu" type="submit" disabled={loading}
                                    sx={{
                                        background:`linear-gradient(135deg,${PRIMARY_COLOR} 0%,${PRIMARY_DARK} 100%)`,
                                        borderRadius:'10px', padding:'12px 0', fontSize:15, fontWeight:600,
                                        textTransform:'none', letterSpacing:'.2px',
                                        boxShadow:'0 4px 14px rgba(26,155,127,.28)',
                                        '&:hover':{ background:`linear-gradient(135deg,${PRIMARY_LIGHT} 0%,${PRIMARY_COLOR} 100%)`, boxShadow:'0 6px 20px rgba(26,155,127,.35)' },
                                        '&:disabled':{ background:'#cbd5e1', color:'#fff' },
                                        transition:'all .25s ease',
                                    }}>
                                    {loading
                                        ? <><CircularProgress size={18} color="inherit" style={{ marginRight:10 }} />Signing in…</>
                                        : <>Sign In <span style={{ marginLeft:8 }}>→</span></>}
                                </Button>

                                <div className="text-center mt-4">
                                    <a href="#" style={{ color:PRIMARY_COLOR, textDecoration:'none', fontSize:13.5, fontWeight:600 }}
                                        onMouseOver={e => { e.target.style.color = PRIMARY_DARK; }}
                                        onMouseOut={e => { e.target.style.color = PRIMARY_COLOR; }}>
                                        <Lock style={{ fontSize:14, marginRight:4, verticalAlign:'-2px' }} /> Forgot password?
                                    </a>
                                </div>

                                <div style={{ marginTop:'2rem', paddingTop:'1.25rem', borderTop:'1px solid #eef2f6', textAlign:'center' }}>
                                    <p style={{ color:'#94a3b8', fontSize:'.7rem', fontWeight:600, margin:0, letterSpacing:'1.2px' }}>POWERED BY</p>
                                    <p style={{ color:'#475569', fontSize:'.85rem', fontWeight:600, margin:'4px 0 0' }}>Solute Cyber Technology Ltd</p>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══ SOP Step Dialog ══ */}
            <SOPStepDialog
                open={sopViewOpen}
                doc={sopViewDoc}
                onClose={() => setSOPViewOpen(false)}
            />

            {/* ══ SOP List Dialog ══ */}
            <SOPListDialog
                open={sopListOpen}
                docs={sopDocs}
                onClose={() => setSOPListOpen(false)}
                onSelect={handleViewSOP}
                loadingId={sopLoadingId}
            />

            {/* ══ First Login Dialog ══ */}
          
<FirstLoginDialog
    open={showFirstLogin}
    email={firstLoginEmail}
    trans_code={pendingUserData?.id}   // ← add this line
    onClose={() => { setShowFirstLogin(false); setLoading(false); }}
    onSuccess={() => {
        setShowFirstLogin(false);
        Swal.fire({ title: 'Password Changed!', text: 'Please login with your new password.', icon: 'success', confirmButtonColor: '#1A9B7F' });
        setFormData({ username: formData.username, password: '' });
        setLoading(false);
    }}
/>
        </div>
    );
}

export default Login;